import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { SearchInput } from "@/components/SearchInput";
import { TruncatedTooltipText } from "@/components/TruncatedTooltipText";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { Switch } from "@/components/Switch";
import { authApi, developmentApi } from "@/lib/api";

export interface ParticipantUser {
    id: number;
    name: string;
    email: string;
}

export interface ParticipantItem {
    id: number;
    user_id: number;
    role: "viewer" | "editor";
    user?: ParticipantUser;
}

interface SystemUser {
    id: number;
    name: string;
    email: string;
    roles?: string[];
}

interface DevelopmentParticipantsProps {
    developmentId: number;
    participants?: ParticipantItem[];
    onUpdate: () => void | Promise<void>;
    isCancelled?: boolean;
}

export const DevelopmentParticipants: React.FC<
    DevelopmentParticipantsProps
> = ({ developmentId, participants = [], onUpdate, isCancelled = false }) => {
    const { t } = useTranslation("global");
    const { isIt, isClient } = useAuth();
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [systemUsers, setSystemUsers] = useState<SystemUser[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<
        Array<"viewer" | "editor">
    >([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
    const [participantToDelete, setParticipantToDelete] =
        useState<ParticipantItem | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState<string>("");

    useEffect(() => {
        if (isAddModalOpen && isIt && systemUsers.length === 0) {
            authApi
                .users()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setSystemUsers(res.data);
                    }
                })
                .catch(() => undefined);
        }
    }, [isAddModalOpen, isIt, systemUsers.length]);

    const handleAdd = async (): Promise<void> => {
        if (selectedUserIds.length === 0 || selectedRoles.length === 0) {
            return;
        }

        const role: "viewer" | "editor" = selectedRoles.includes("editor")
            ? "editor"
            : "viewer";

        setSubmitting(true);
        setError(null);

        try {
            for (const userId of selectedUserIds) {
                const res = await developmentApi.addParticipant(
                    developmentId,
                    userId,
                    role
                );
                if (!res.result) {
                    throw new Error(res.message);
                }
            }

            setIsAddModalOpen(false);
            setSelectedUserIds([]);
            setSelectedRoles([]);
            await onUpdate();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Error al añadir participantes"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleRole = async (
        userId: number,
        currentRole: "viewer" | "editor"
    ): Promise<void> => {
        const nextRole: "viewer" | "editor" =
            currentRole === "editor" ? "viewer" : "editor";
        setUpdatingUserId(userId);

        try {
            const res = await developmentApi.addParticipant(
                developmentId,
                userId,
                nextRole
            );
            if (res.result) {
                await onUpdate();
            }
        } catch (err) {
            alert(err instanceof Error ? err.message : "Error al cambiar rol");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const handleConfirmRemove = async (): Promise<void> => {
        if (!participantToDelete) {
            return;
        }

        setIsDeleting(true);

        try {
            const res = await developmentApi.removeParticipant(
                developmentId,
                participantToDelete.user_id
            );
            if (res.result) {
                setParticipantToDelete(null);
                await onUpdate();
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Error al eliminar participante"
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const setRoleChecked = (
        role: "viewer" | "editor",
        checked: boolean
    ): void => {
        setSelectedRoles((current) => {
            if (checked) {
                return current.includes(role) ? current : [...current, role];
            }
            return current.filter((item) => item !== role);
        });
    };

    // Filter users not already added
    const existingUserIds = new Set(participants.map((p) => p.user_id));
    const availableUsers = systemUsers.filter(
        (u) => !existingUserIds.has(u.id)
    );

    const availableUserOptions = availableUsers.map((u) => ({
        id: u.id,
        name: u.name,
        subtitle: u.email,
    }));

    const filteredParticipants = useMemo(() => {
        const needle = search.trim().toLocaleLowerCase("es");
        if (!needle) {
            return participants;
        }

        return participants.filter((participant) => {
            const roleLabel =
                participant.role === "editor"
                    ? t("developments.detail.role_editor")
                    : t("developments.detail.role_viewer");

            return `${participant.user?.name ?? ""} ${participant.user?.email ?? ""} ${roleLabel}`
                .toLocaleLowerCase("es")
                .includes(needle);
        });
    }, [participants, search, t]);

    if (isClient) {
        return null;
    }

    return (
        <section className="bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs">
            <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                className="flex w-full items-center justify-between pb-1 cursor-pointer select-none"
                aria-expanded={isOpen}
            >
                <div className="flex min-w-0 items-center gap-2">
                    <Icon
                        value="userCheck"
                        size={17}
                        className="text-primary"
                    />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                        {t("developments.detail.participants_title")}
                    </h3>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="primary" size="sm" count>
                        {participants.length}
                    </Badge>
                    <Icon
                        value="chevronRight"
                        size={14}
                        className={`text-slate-400 transition-transform duration-300 ease-in-out ${isOpen ? "rotate-90" : ""}`}
                    />
                </div>
            </button>

            <div
                className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
            >
                <div className="overflow-hidden">
                    <div className="space-y-3 pt-3">
                        {isIt && !isCancelled && (
                            <button
                                type="button"
                                onClick={() => setIsAddModalOpen(true)}
                                className="btn-primary w-full justify-center h-9 text-xs shadow-xs"
                            >
                                <Icon value="userPlus" size={15} />
                                <span>
                                    {t("developments.detail.add_participant")}
                                </span>
                            </button>
                        )}

                        {participants.length > 0 && (
                            <SearchInput
                                value={search}
                                onChange={setSearch}
                                placeholder={t(
                                    "developments.detail.section_search_placeholder"
                                )}
                                wrapperClassName="px-0.5"
                            />
                        )}

                        <div className="space-y-2">
                            {participants.length === 0 ? (
                                <p className="py-3 text-center text-xs text-slate-400">
                                    {t("developments.detail.participant_empty")}
                                </p>
                            ) : filteredParticipants.length === 0 ? (
                                <p className="py-3 text-center text-xs text-slate-400">
                                    {t(
                                        "developments.detail.section_search_empty"
                                    )}
                                </p>
                            ) : (
                                filteredParticipants.map((p) => {
                                    const isEditor = p.role === "editor";
                                    const isUpdating =
                                        updatingUserId === p.user_id;

                                    return (
                                        <div
                                            key={p.id}
                                            className="flex min-w-0 items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs"
                                        >
                                            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                                                {isIt && !isCancelled ? (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleToggleRole(
                                                                p.user_id,
                                                                p.role
                                                            )
                                                        }
                                                        disabled={isUpdating}
                                                        className="shrink-0 cursor-pointer rounded-md p-0.5 transition-colors hover:bg-slate-200/70 disabled:opacity-50 dark:hover:bg-white/10"
                                                        title={
                                                            isEditor
                                                                ? t(
                                                                      "developments.detail.switch_to_viewer"
                                                                  )
                                                                : t(
                                                                      "developments.detail.switch_to_editor"
                                                                  )
                                                        }
                                                    >
                                                        <Icon
                                                            value={
                                                                isEditor
                                                                    ? "edit"
                                                                    : "eye"
                                                            }
                                                            size={17}
                                                            className={
                                                                isEditor
                                                                    ? "text-amber-600 dark:text-amber-400"
                                                                    : "text-blue-600 dark:text-blue-400"
                                                            }
                                                        />
                                                    </button>
                                                ) : (
                                                    <Icon
                                                        value={
                                                            isEditor
                                                                ? "edit"
                                                                : "eye"
                                                        }
                                                        size={17}
                                                        className={`shrink-0 ${isEditor ? "text-amber-600 dark:text-amber-400" : "text-blue-600 dark:text-blue-400"}`}
                                                    />
                                                )}
                                                <div className="min-w-0 flex-1 overflow-hidden">
                                                    <TruncatedTooltipText
                                                        text={
                                                            p.user?.name ??
                                                            `User #${p.user_id}`
                                                        }
                                                        className="font-semibold text-slate-800"
                                                    />
                                                    {p.user?.email && (
                                                        <TruncatedTooltipText
                                                            text={p.user.email}
                                                            className="text-[10px] text-slate-400"
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 shrink-0">
                                                {isIt && !isCancelled ? (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleToggleRole(
                                                                p.user_id,
                                                                p.role
                                                            )
                                                        }
                                                        disabled={isUpdating}
                                                        className="group inline-flex items-center gap-1 cursor-pointer transition-transform active:scale-95 disabled:opacity-50"
                                                        title={
                                                            isEditor
                                                                ? t(
                                                                      "developments.detail.switch_to_viewer"
                                                                  )
                                                                : t(
                                                                      "developments.detail.switch_to_editor"
                                                                  )
                                                        }
                                                    >
                                                        <Badge
                                                            variant={
                                                                isEditor
                                                                    ? "warning"
                                                                    : "info"
                                                            }
                                                            size="md"
                                                            className="px-2.5 font-bold uppercase tracking-wider group-hover:opacity-80 transition-all cursor-pointer flex items-center gap-1"
                                                        >
                                                            {/*<Icon value={isEditor ? 'edit' : 'eye'} size={12} />*/}
                                                            <span>
                                                                {isEditor
                                                                    ? t(
                                                                          "developments.detail.role_editor"
                                                                      )
                                                                    : t(
                                                                          "developments.detail.role_viewer"
                                                                      )}
                                                            </span>
                                                        </Badge>
                                                    </button>
                                                ) : (
                                                    <Badge
                                                        variant={
                                                            isEditor
                                                                ? "warning"
                                                                : "info"
                                                        }
                                                        size="md"
                                                        className="px-2.5 font-bold uppercase tracking-wider flex items-center gap-1"
                                                    >
                                                        <Icon
                                                            value={
                                                                isEditor
                                                                    ? "edit"
                                                                    : "eye"
                                                            }
                                                            size={12}
                                                        />
                                                        <span>
                                                            {isEditor
                                                                ? t(
                                                                      "developments.detail.role_editor"
                                                                  )
                                                                : t(
                                                                      "developments.detail.role_viewer"
                                                                  )}
                                                        </span>
                                                    </Badge>
                                                )}

                                                {isIt && !isCancelled && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setParticipantToDelete(
                                                                p
                                                            )
                                                        }
                                                        className="p-1.5 text-rose-600 rounded-md transition-colors cursor-pointer hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
                                                        title={t(
                                                            "modal.delete"
                                                        )}
                                                    >
                                                        <Icon
                                                            value="trash"
                                                            size={16}
                                                        />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isIt && (
                <Modal
                    isOpen={isAddModalOpen}
                    onClose={() => {
                        setIsAddModalOpen(false);
                        setSelectedUserIds([]);
                        setError(null);
                    }}
                    title={t("developments.detail.add_participant")}
                    maxWidth="md"
                    onSubmit={handleAdd}
                    isSubmitting={submitting}
                    submitLabel={t(
                        "developments.detail.add_participant_submit"
                    )}
                    submitDisabled={
                        selectedUserIds.length === 0 ||
                        selectedRoles.length === 0
                    }
                >
                    <div className="space-y-4 min-h-[220px]">
                        {error && (
                            <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
                                {error}
                            </div>
                        )}

                        <div>
                            <SearchableMultiSelect
                                label={t("developments.detail.user_select")}
                                options={availableUserOptions}
                                value={selectedUserIds}
                                onChange={setSelectedUserIds}
                                placeholder={t(
                                    "developments.detail.user_select"
                                )}
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                                {t("developments.detail.role_select")}
                            </label>
                            <div className="rounded-xl border border-slate-200/80 dark:border-white/10 bg-slate-50/70 dark:bg-dark-subtle/40 p-2 space-y-1">
                                {(["viewer", "editor"] as const).map((role) => {
                                    const checked =
                                        selectedRoles.includes(role);
                                    return (
                                        <div
                                            key={role}
                                            className={`rounded-lg px-3 py-2 transition-colors ${
                                                checked
                                                    ? "bg-white dark:bg-dark-box shadow-xs"
                                                    : "hover:bg-white/70 dark:hover:bg-dark-box/60"
                                            }`}
                                        >
                                            <Switch
                                                checked={checked}
                                                onChange={(next) =>
                                                    setRoleChecked(role, next)
                                                }
                                                label={t(
                                                    `developments.detail.role_${role}`
                                                )}
                                                size="sm"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {isIt && (
                <Modal
                    isOpen={Boolean(participantToDelete)}
                    onClose={() => {
                        if (!isDeleting) {
                            setParticipantToDelete(null);
                        }
                    }}
                    title={t("developments.detail.remove_participant_title")}
                    maxWidth="sm"
                    submitVariant="danger"
                    onSubmit={handleConfirmRemove}
                    isSubmitting={isDeleting}
                    submitLabel={t("modal.delete")}
                    submittingLabel={t("modal.deleting")}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="space-y-3 py-2 text-center">
                        <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                            {t("developments.detail.remove_participant_desc")}
                        </p>
                        <div className="space-y-1 min-w-0 px-2">
                            <TruncatedTooltipText
                                text={
                                    participantToDelete?.user?.name ??
                                    `User #${participantToDelete?.user_id}`
                                }
                                className="text-sm font-semibold text-slate-900 dark:text-white"
                            />
                            {participantToDelete?.user?.email && (
                                <TruncatedTooltipText
                                    text={participantToDelete.user.email}
                                    className="text-xs text-slate-500 dark:text-zinc-400"
                                />
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </section>
    );
};
